import { Card, Col, Collapse, Row, Space, Tag, Typography } from "antd";
import {
  CarOutlined,
  CustomerServiceOutlined,
  FileProtectOutlined,
  FolderOpenOutlined,
  MailOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const systemSections = [
  {
    title: "Inventory",
    description: "Add vehicles, manage photos, pricing, mileage, status, and the key details shown on vehicle cards.",
    icon: <CarOutlined />,
  },
  {
    title: "Vehicle Details",
    description: "Review full vehicle records, photo galleries, appraisal notes, merchandising context, and customer-facing information.",
    icon: <FileProtectOutlined />,
  },
  {
    title: "Customer Inquiries",
    description: "Track buyer interest, availability requests, follow-up status, notes, and customer contact details.",
    icon: <CustomerServiceOutlined />,
  },
  {
    title: "Documents",
    description: "Organize workspace files, vehicle documents, downloads, and shared dealership resources.",
    icon: <FolderOpenOutlined />,
  },
];

const contactItems = [
  {
    title: "Product Support",
    detail: "Use this when inventory, photos, inquiries, documents, or workspace setup do not behave as expected.",
    action: "support@oliveeb.com",
    icon: <CustomerServiceOutlined />,
  },
  {
    title: "Account & Access",
    detail: "Use this for invitations, workspace membership, roles, permissions, login, or account security issues.",
    action: "security@oliveeb.com",
    icon: <SafetyCertificateOutlined />,
  },
  {
    title: "Urgent Operations",
    detail: "For urgent operational questions, include the workspace name, affected user, page URL, and exact time of the issue.",
    action: "206-555-0198",
    icon: <PhoneOutlined />,
  },
];

const faqItems = [
  {
    key: "workspace",
    label: "Why do records depend on my selected workspace?",
    children: "Oliveeb Auto is workspace-based. Vehicles, inquiries, documents, access control, billing, and organization settings are filtered by the current workspace so users only see records tied to their membership.",
  },
  {
    key: "roles",
    label: "Who can manage administrative features?",
    children: "Auto owners and auto admins can manage workspace operations such as inventory changes, access control, subscriptions, organization settings, website tools, and administrative actions.",
  },
  {
    key: "documents",
    label: "How do Documents work?",
    children: "Documents are split into public documents and internal workspace documents. Internal documents can be uploaded into folders, previewed, downloaded, moved by drag and drop, and shared with bookmarkable links. Public documents are shared Oliveeb resources.",
  },
  {
    key: "photos",
    label: "Why does the inventory list only show one photo?",
    children: "Inventory cards load only the main photo so the list stays fast. Open a vehicle detail page to load and review the full photo gallery.",
  },
  {
    key: "availability",
    label: "Where do Check Availability requests go?",
    children: "Check Availability creates a customer inquiry for the selected vehicle. You can review and update inquiry status from Customer Inquiries.",
  },
  {
    key: "support",
    label: "What should I include when contacting support?",
    children: "Include the page URL, workspace name, vehicle stock or VIN if relevant, what you expected to happen, what actually happened, and the approximate time. Screenshots help when the issue is visual.",
  },
];

const Help = () => (
  <div className="project-page help-center-page">
    <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
      <WidgetSectionTitle
        icon={<QuestionCircleOutlined />}
        title="Help Center"
        subtitle="Guidance for Oliveeb Auto workflows, support, and common questions"
        className="flex-1"
        titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
        subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
      />
    </div>

    <Card className="help-center-hero">
      <Space direction="vertical" size={10}>
        <Tag color="blue">Oliveeb Auto</Tag>
        <Title level={3}>Dealership inventory and customer inquiry tools in one workspace.</Title>
        <Paragraph>
          Oliveeb Auto helps dealerships manage vehicle listings, photos, customer inquiries, workspace access, documents,
          websites, billing, and everyday inventory workflows.
        </Paragraph>
      </Space>
    </Card>

    <section className="help-center-section">
      <Title level={4}>About the System</Title>
      <Row gutter={[16, 16]}>
        {systemSections.map((item) => (
          <Col xs={24} md={12} xl={6} key={item.title}>
            <Card className="help-center-card">
              <Space direction="vertical" size={12}>
                <span className="help-center-icon">{item.icon}</span>
                <Text strong>{item.title}</Text>
                <Paragraph type="secondary">{item.description}</Paragraph>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </section>

    <section className="help-center-section">
      <Title level={4}>Contact Support</Title>
      <Row gutter={[16, 16]}>
        {contactItems.map((item) => (
          <Col xs={24} lg={8} key={item.title}>
            <Card className="help-center-contact-card">
              <Space direction="vertical" size={10}>
                <span className="help-center-icon blue">{item.icon}</span>
                <Text strong>{item.title}</Text>
                <Paragraph type="secondary">{item.detail}</Paragraph>
                <Text className="help-center-contact-value">
                  {item.action.includes("@") ? <MailOutlined /> : <PhoneOutlined />} {item.action}
                </Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </section>

    <section className="help-center-section">
      <Title level={4}>Frequently Asked Questions</Title>
      <Collapse className="help-center-faq" items={faqItems} bordered={false} />
    </section>

    <Card className="help-center-footer-card">
      <Space align="start" size={12}>
        <span className="help-center-icon"><TeamOutlined /></span>
        <div>
          <Text strong>Need hands-on help?</Text>
          <Paragraph type="secondary">
            Send support the workspace name and the exact page URL. For vehicle issues, include the stock number, VIN, or vehicle
            title needed to locate the record.
          </Paragraph>
        </div>
      </Space>
    </Card>
  </div>
);

export default Help;
